import { useState } from 'react';
import { sx } from '../../ui/designStyles';
import { CLOSE_BTN, KICKER, PRI, SEC, WHY_BG, badgeInline, chip, csvLine, download, isEst, moneyC, pre, railStyle, sigStyle, spark, sparkPair, type Honesty } from './lib';
import { Est } from '../../ui/buildLabels';
import { matchQ, sortRows, type Lens, type LensRow, type PayerPick, type Period, type SortKey } from './model';
import type { Tag } from './GenTab';

export interface SnapKpi {
  k: string;
  b: Honesty | null;
  v: string;
  d: string;
  dc: string;
  s: string;
  vc: string;
  sp: number[];
  sc: string;
}
export interface TodayItem {
  k: string;
  n: string;
  t: string;
  sub: string;
  /** Names in front of the sub line, never translated. */
  subLead?: string;
  v: string;
  vc: string;
  btn: string;
  urgent?: boolean;
  go: () => void;
  /** A write: off for a read-only role. */
  write?: boolean;
}
export interface QuickList {
  title: string;
  items: { a: string; b: string; act: string }[];
}

const KPI_BOX = 'min-width:0;padding:5px 10px;display:flex;flex-direction:column;gap:0;border-left:1px solid rgba(34,39,46,.08);';
const SEP = 'width:1px;height:20px;background:rgba(34,39,46,.15);margin:0 4px;';
const COLS = 'minmax(0,1.25fr) 84px 78px 74px 64px 50px 40px 60px 64px minmax(0,1fr)';
/** V9 (capture.js allCols): five columns until Show all columns opens the rest. */
const COLS_FEW = 'minmax(0,1.4fr) 120px 110px 100px 110px';
export const KEEP_COLS: SortKey[] = ['name', 'cap', 'bill', 'paid', 'gap'];

/**
 * Capture › Snapshot (design pvOv): six figures with their 12-month lines, Today's top
 * three, the lens table (by code, payer, practitioner, wing) and the quick-look rail.
 */
export function Snapshot(p: {
  kpis: SnapKpi[];
  today: TodayItem[];
  done: Record<string, boolean>;
  rows: LensRow[];
  lens: Lens;
  setLens: (l: Lens) => void;
  pt: PayerPick;
  setPt: (p: PayerPick) => void;
  per: Period;
  setPer: (p: Period) => void;
  tag: Tag;
  narrow: boolean;
  railOpen: boolean;
  setRailOpen: (o: boolean) => void;
  roReason: string | null;
  /** The rail's list for the picked row (blocked residents, open items, panel). */
  quickList: (r: LensRow) => QuickList;
  /** The best published rate line for a code. */
  best: (r: LensRow) => string;
  primary: { t: string; go: () => void };
  askWhy: (r: LensRow) => void;
  measured: Honesty | null;
  modeled: Honesty | null;
  /** Care delivered's tag, from the capture summary (the Snapshot tile's own): the quick look's Delivered and Not billed wear it too. */
  delivered: Honesty | null;
  tagSuffix: string;
  emptyText: string;
  /** The live capture feed's current line (V6 LIVE line), or null with no events. */
  live?: { text: string; ago: string } | null;
}) {
  const [q, setQ] = useState('');
  const [sk, setSk] = useState<SortKey>('gap');
  const [sd, setSd] = useState<1 | -1>(-1);
  const [allCols, setAllCols] = useState(false);
  const grid = allCols ? 'min-width:780px;grid-template-columns:' + COLS + ';' : 'min-width:520px;grid-template-columns:' + COLS_FEW + ';';
  const [sel, setSel] = useState<Partial<Record<Lens, string>>>({});
  const list = sortRows(p.rows.filter((r) => matchQ(r, q)), sk, sd);
  const selId = sel[p.lens] ?? list[0]?.id;
  const cur = p.rows.find((r) => r.id === selId) ?? list[0] ?? null;
  const left = p.today.filter((a) => !p.done[a.k]).length;
  const H: [SortKey, string, 'left' | 'right'][] = [
    ['name', { code: 'Code', payer: 'Payer', prac: 'Practitioner', wing: 'Wing' }[p.lens], 'left'],
    ['cap', 'Enrolled / eligible', 'left'],
    ['bill', 'Practitioner bills', 'right'],
    ['share', "Building's share", 'right'],
    ['paid', 'Paid', 'right'],
    ['den', 'Denied', 'right'],
    ['dtp', 'Days', 'right'],
    ['g', '12 mo', 'left'],
    ['gap', 'Not billed', 'right'],
    ['sig', 'Signal', 'left'],
  ];
  const tot = list.reduce((a, r) => ({ del: a.del + (r.delivered ?? 0), bill: a.bill + r.bill, paid: a.paid + r.paid }), { del: 0, bill: 0, paid: 0 });
  const foot = list.length + ' rows · delivered ' + moneyC(tot.del) + ' · billed ' + moneyC(tot.bill) + ' · paid ' + moneyC(tot.paid) + p.tagSuffix;
  const exportCsv = () =>
    download(
      'capture-' + p.lens + '-' + p.per.replace(/ /g, '') + '.csv',
      [csvLine(H.map((h) => h[1])), ...list.map((r) => csvLine([r.name + (r.sub ? ' · ' + r.sub : ''), r.elig != null ? r.enr + ' / ' + r.elig : '', (r.bill / 100).toFixed(2), r.share == null ? r.shareReason ?? '' : (r.share / 100).toFixed(2), (r.paid / 100).toFixed(2), r.den == null ? '' : r.den.toFixed(1) + '%', '', '', r.gap == null ? '' : (r.gap / 100).toFixed(2), r.sig?.text ?? '']))].join('\n'),
      'text/csv',
    );
  const ql = cur
    ? (() => {
        const gapTxt = cur.gap != null && cur.gap > 0 ? moneyC(cur.gap) + ' of care eligible was not billed.' : '';
        const why = cur.sig ? cur.sig.text + '. ' + gapTxt : gapTxt || 'Nothing stands out for this row.';
        const delivered = cur.trend.length ? null : null;
        const [spD, spB] = sparkPair(delivered ?? [], cur.trend, 274, 64);
        const lastB = cur.trend[cur.trend.length - 1] ?? 0;
        const lst = p.quickList(cur);
        return { why: why.trim(), whyBg: WHY_BG[cur.sig?.k ?? 'n'], spD, spB, lastB, lst };
      })()
    : null;

  return (
    <div data-screen-label="Capture Snapshot" style={sx('flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:10px;')}>
      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(165px,1fr));border-top:1px solid rgba(34,39,46,.12);border-bottom:1px solid rgba(34,39,46,.12);')}>
        {p.kpis.map((k) => {
          const b = p.tag(k.b);
          return (
            <div key={k.k} style={sx(KPI_BOX)} data-testid={'capture-kpi'}>
              <span style={sx('display:flex;align-items:center;flex-wrap:wrap;gap:2px 5px;min-width:0;')}>
                <span style={sx('flex:1 1 auto;line-height:1.25;font-size:14px;font-weight:700;color:#3C454F;')}>{k.k}</span>
                {b && !isEst(b) && <span style={sx('flex:0 0 auto;' + badgeInline(b))} data-honesty={b}>{b}</span>}
              </span>
              <span style={sx('display:flex;align-items:flex-end;justify-content:space-between;flex-wrap:wrap;gap:2px 6px;')}>
                <span style={sx('white-space:nowrap;font-size:' + (/^[A-Za-z]/.test(k.v) ? '14px' : '17px') + ';line-height:1.25;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:' + k.vc + ';')}>{k.v}{isEst(b) && <Est />}</span>
                <svg width="52" height="16" viewBox="0 0 60 20" style={sx('flex:0 0 52px;')} aria-hidden="true">
                  <polyline points={spark(k.sp, 60, 20)} fill="none" stroke={k.sc} strokeWidth="1.5" strokeLinejoin="round" />
                </svg>
              </span>
              <span style={sx('font-size:14px;line-height:1.3;color:#54606C;text-wrap:pretty;')}>
                {k.d && <span style={sx('font-weight:700;color:' + k.dc + ';')}>{k.d}</span>}
                {k.d ? ' ' : ''}
                {k.s}
              </span>
            </div>
          );
        })}
      </div>

      {p.live && (
        <div data-testid="capture-live" style={sx('flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:4px 8px;min-width:0;font-size:14px;color:#54606C;')}>
          <span style={sx('width:7px;height:7px;border-radius:50%;background:#1F6B52;flex:0 0 7px;')} />
          <span style={sx('font-size:14px;font-weight:800;letter-spacing:.08em;color:#1F6B52;')}>LIVE</span>
          <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;')}>{p.live.text}</span>
          <span>{p.live.ago}</span>
        </div>
      )}
      <div style={sx('flex:0 0 auto;display:flex;flex-wrap:wrap;align-items:stretch;gap:8px;')}>
        <div style={sx('flex:0 0 auto;display:flex;flex-direction:column;justify-content:center;padding-right:4px;')}>
          <span style={sx('font-size:16px;font-weight:800;')}>Today</span>
          <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{left ? left + ' left' : 'All done for today'}</span>
        </div>
        {p.today.map((a) => {
          const d = !!p.done[a.k];
          const off = a.write ? p.roReason : null;
          return (
            <div
              key={a.k}
              style={sx('flex:1 1 300px;min-width:0;display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:8px;border:1px solid ' + (a.urgent && !d ? 'rgba(179,38,30,.35)' : 'rgba(34,39,46,.12)') + ';background:' + (d ? 'rgba(31,107,82,.06)' : '#FCFAF4') + ';')}
            >
              <span style={sx('flex:0 0 22px;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;background:' + (d ? '#1F6B52' : a.urgent ? '#B3261E' : '#22272E') + ';color:#FFFFFF;')}>{d ? '✓' : a.n}</span>
              <span style={sx('flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:1px;')}>
                <span title={a.t} style={sx('font-size:16px;font-weight:700;line-height:1.3;overflow-wrap:anywhere;text-wrap:pretty;text-decoration:' + (d ? 'line-through' : 'none') + ';')}>{d ? 'Done · ' + a.t : a.t}</span>
                <span style={sx('font-size:14px;line-height:1.3;color:#54606C;overflow-wrap:anywhere;text-wrap:pretty;')} title={(a.subLead ? a.subLead + ' · ' : '') + a.sub}>
                  {a.subLead && <span translate="no">{a.subLead}</span>}
                  {a.subLead ? ' · ' : ''}
                  <span>{a.sub}</span>
                </span>
              </span>
              <span style={sx('flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:4px;')}>
                <span style={sx('font-size:16px;font-weight:800;font-variant-numeric:tabular-nums;color:' + a.vc + ';white-space:nowrap;')}>{d ? '' : a.v}</span>
                {d ? (
                  <span style={sx('height:28px;padding:0 10px;display:inline-flex;align-items:center;border-radius:6px;font-size:14px;font-weight:700;white-space:nowrap;text-decoration:none;color:#2F5C97;')}>Sent</span>
                ) : (
                  <a
                    href="#"
                    onClick={(e) => { pre(e); if (!off) a.go(); }}
                    aria-disabled={off ? 'true' : undefined}
                    title={off ?? undefined}
                    style={sx('height:28px;padding:0 10px;display:inline-flex;align-items:center;border-radius:6px;font-size:14px;font-weight:700;white-space:nowrap;text-decoration:none;background:#22272E;color:#F6F3EC;' + (off ? 'opacity:.55;cursor:not-allowed;' : ''))}
                  >
                    {a.btn}
                  </a>
                )}
              </span>
            </div>
          );
        })}
        {!p.today.length && <span style={sx('align-self:center;font-size:16px;color:#54606C;')}>Nothing is waiting on you today.</span>}
      </div>

      <div style={sx('flex:0 0 auto;display:flex;align-items:center;flex-wrap:wrap;gap:6px;')}>
        {([['code', 'By code'], ['payer', 'By payer'], ['prac', 'By practitioner'], ['wing', 'By wing']] as const).map(([k, n]) => (
          <a key={k} href="#" onClick={(e) => { pre(e); p.setLens(k); setSk('gap'); setSd(-1); }} style={sx(chip(p.lens === k))} aria-pressed={p.lens === k}>{n}</a>
        ))}
        <span style={sx(SEP)} />
        <span style={sx('font-size:14px;color:#54606C;')}>Payer</span>
        {(['All', 'FFS', 'MA', 'Medicaid', 'Comm'] as const).map((n) => (
          <a key={n} href="#" onClick={(e) => { pre(e); p.setPt(n); }} style={sx(chip(p.pt === n))} aria-pressed={p.pt === n}>{n === 'Comm' ? 'Commercial' : n}</a>
        ))}
        <span style={sx(SEP)} />
        {(['Month', 'Quarter', '12 mo'] as const).map((n) => (
          <a key={n} href="#" onClick={(e) => { pre(e); p.setPer(n); }} style={sx(chip(p.per === n))} aria-pressed={p.per === n}>{n}</a>
        ))}
        <span style={sx('flex:1 1 10px;')} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search code, payer, name"
          aria-label="Search code, payer, name"
          style={sx('width:200px;height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;font-family:Archivo,sans-serif;font-size:16px;background:#FCFAF4;color:#22272E;outline:none;')}
        />
      </div>

      <div style={sx('flex:1 1 auto;min-height:360px;display:grid;grid-template-columns:' + (p.narrow ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(240px,28%)') + ';gap:12px;position:relative;')}>
        <div
          onClickCapture={(e) => { if (p.narrow && !p.railOpen && (e.target as HTMLElement).closest?.('a[data-row]')) p.setRailOpen(true); }}
          style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;border:1px solid rgba(34,39,46,.12);border-radius:8px;background:#FCFAF4;overflow:auto;')}
        >
          <div style={sx('flex:0 0 auto;display:flex;justify-content:flex-end;padding:6px 10px 0;')}>
            <a
              href="#"
              data-testid="capture-cols"
              onClick={(e) => { pre(e); setAllCols((o) => !o); }}
              aria-pressed={allCols}
              style={sx('display:inline-flex;align-items:center;min-height:32px;padding:0 12px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.35);')}
            >
              {allCols ? 'Show fewer columns' : 'Show all columns'}
            </a>
          </div>
          <div style={sx('flex:0 0 auto;position:sticky;top:0;z-index:2;' + grid + 'display:grid;gap:0 8px;padding:0 10px;background:#F6F3EC;border-bottom:1px solid rgba(34,39,46,.14);')}>
            {H.filter(([key]) => allCols || KEEP_COLS.includes(key)).map(([key, t, al]) => {
              const on = sk === key;
              return (
                <a
                  key={key}
                  href="#"
                  onClick={(e) => { pre(e); setSk(key); setSd(on ? (-sd as 1 | -1) : -1); }}
                  aria-sort={on ? (sd < 0 ? 'descending' : 'ascending') : undefined}
                  style={sx('padding:5px 0;font-size:14px;line-height:1.2;font-weight:700;text-decoration:none;align-self:end;text-align:' + al + ';color:' + (on ? '#22272E' : '#54606C') + ';')}
                >
                  {t + (on ? (sd < 0 ? ' ↓' : ' ↑') : '')}
                </a>
              );
            })}
          </div>
          <div style={sx('flex:1 0 auto;')}>
            {list.map((r) => {
              const on = cur?.id === r.id;
              const cap = r.elig ? Math.round(((r.enr ?? 0) / r.elig) * 100) : 0;
              return (
                <a
                  key={r.id}
                  href="#"
                  data-row="1"
                  aria-current={on ? 'true' : undefined}
                  onClick={(e) => { pre(e); setSel((s) => ({ ...s, [p.lens]: r.id })); }}
                  style={sx(grid + 'display:grid;gap:0 8px;align-items:center;padding:6px 10px;text-decoration:none;color:inherit;border-bottom:1px solid rgba(34,39,46,.07);border-left:3px solid ' + (on ? '#2F5C97' : 'transparent') + ';background:' + (on ? 'rgba(47,92,151,.07)' : 'transparent') + ';')}
                >
                  <span style={sx('min-width:0;display:flex;flex-direction:column;')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{r.name}</span>
                    <span style={sx('font-size:14px;line-height:1.3;color:#54606C;')}>{parts(r.subParts)}</span>
                  </span>
                  <span style={sx('display:flex;flex-direction:column;gap:3px;')}>
                    <span style={sx('font-size:14px;font-variant-numeric:tabular-nums;color:#22272E;')}>{r.elig != null ? (r.enr ?? 0) + ' / ' + r.elig + (r.elig ? ' · ' + cap + '%' : '') : '–'}</span>
                    <span style={sx('position:relative;height:5px;border-radius:3px;background:rgba(34,39,46,.1);')}>
                      <span style={sx('position:absolute;left:0;top:0;bottom:0;width:' + cap + '%;border-radius:3px;background:#2F5C97;')} />
                    </span>
                  </span>
                  <span style={sx('font-size:16px;font-variant-numeric:tabular-nums;text-align:right;color:#22272E;')}>{r.bill ? moneyC(r.bill) : '$0'}</span>
                  {allCols && (
                    <span style={sx('font-size:14px;line-height:1.2;font-variant-numeric:tabular-nums;text-align:right;color:' + (r.share == null ? '#6B4A12' : '#22272E') + ';')}>
                      {r.share == null ? (r.bill ? 'No agreement' : '–') : moneyC(r.share)}
                    </span>
                  )}
                  <span style={sx('font-size:16px;font-variant-numeric:tabular-nums;text-align:right;color:#22272E;')}>{r.paid ? moneyC(r.paid) : '$0'}</span>
                  {allCols && (
                    <>
                      <span style={sx('font-size:14px;font-variant-numeric:tabular-nums;text-align:right;color:' + (r.den != null && r.den >= 6 ? '#B3261E' : '#3C454F') + ';')}>{r.den ? r.den.toFixed(1) + '%' : '–'}</span>
                      <span style={sx('font-size:14px;font-variant-numeric:tabular-nums;text-align:right;color:#3C454F;')}>{r.dtp ? r.dtp + ' d' : '–'}</span>
                      <svg width="60" height="20" viewBox="0 0 60 20" aria-hidden="true">
                        <polyline points={r.bill ? spark(r.trend, 64, 20) : '0,18 64,18'} fill="none" stroke={r.trend.length > 1 && r.trend[r.trend.length - 1] < r.trend[0] ? '#B3261E' : '#2F5C97'} strokeWidth="1.5" strokeLinejoin="round" />
                      </svg>
                    </>
                  )}
                  <span style={sx('font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;text-align:right;color:' + (r.gap ? '#6B4A12' : '#54606C') + ';')}>{r.gap ? moneyC(r.gap) : '–'}</span>
                  {allCols && <span style={sx('min-width:0;')}>{r.sig && <span style={sx(sigStyle(r.sig.k))}>{r.sig.text}</span>}</span>}
                </a>
              );
            })}
            {!list.length && <div style={sx('padding:24px;font-size:16px;color:#54606C;')}>{p.rows.length ? 'Nothing matches. Clear the search or pick another payer.' : p.emptyText}</div>}
          </div>
          <div style={sx('flex:0 0 auto;display:flex;gap:14px;align-items:center;padding:6px 10px;border-top:1px solid rgba(34,39,46,.12);font-size:14px;color:#54606C;')}>
            <span>{foot}</span>
            <span style={sx('flex:1 1 auto;')} />
            <span>The enrolled practitioner bills · Building's share per your signed agreement · Documentation support by Aithora</span>
          </div>
        </div>

        <div style={sx(railStyle(p.narrow, p.railOpen))} data-testid="capture-rail">
          {p.narrow && <a href="#" onClick={(e) => { pre(e); p.setRailOpen(false); }} style={sx(CLOSE_BTN)}>Close ✕</a>}
          {!cur && <span style={sx('font-size:16px;color:#54606C;')}>{p.emptyText}</span>}
          {cur && ql && (
            <>
              <div style={sx('display:flex;flex-direction:column;gap:2px;')}>
                <span style={sx(KICKER)}>QUICK LOOK</span>
                <span style={sx('font-size:17px;font-weight:800;line-height:1.2;')}>
                  {cur.name}
                  {p.lens === 'code' && cur.subParts[0] ? ' ' : ''}
                  {p.lens === 'code' && cur.subParts[0] ? <span>{cur.subParts[0]}</span> : null}
                </span>
                <span style={sx('font-size:14px;color:#54606C;')}>{parts(p.lens === 'code' ? cur.subParts.slice(1) : cur.subParts)}</span>
              </div>
              <span style={sx('font-size:16px;line-height:1.4;color:#22272E;padding:8px 10px;border-radius:7px;background:' + ql.whyBg + ';')}>{ql.why}</span>
              <div style={sx('display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 12px;')}>
                {[
                  { k: 'Delivered', b: cur.delivered == null ? null : p.delivered, v: cur.delivered == null ? '–' : moneyC(cur.delivered), c: '#22272E' },
                  { k: 'Practitioner bills', b: p.measured, v: moneyC(cur.bill), c: '#22272E' },
                  { k: "Building's share", b: cur.share == null ? null : p.measured, v: cur.share == null ? 'None on file' : moneyC(cur.share), c: cur.share == null ? '#54606C' : '#22272E' },
                  { k: 'Not billed', b: cur.gap == null ? null : cur.delivered != null ? p.delivered : p.modeled, v: cur.gap == null ? '–' : moneyC(cur.gap), c: '#6B4A12' },
                ].map((s) => {
                  const b = p.tag(s.b);
                  return (
                    <div key={s.k} data-testid="capture-ql-figure" style={sx('display:flex;flex-direction:column;')}>
                      <span style={sx('display:flex;flex-wrap:wrap;row-gap:2px;gap:2px 5px;align-items:center;font-size:14px;font-weight:700;color:#3C454F;')}>
                        {s.k} {b && !isEst(b) && <span style={sx(badgeInline(b))}>{b}</span>}
                      </span>
                      <span style={sx('font-size:17px;font-weight:800;font-variant-numeric:tabular-nums;color:' + s.c + ';')}>{s.v}{isEst(b) && <Est />}</span>
                    </div>
                  );
                })}
              </div>
              <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
                <span style={sx('display:flex;gap:10px;font-size:14px;color:#54606C;')}>
                  <span style={sx('font-weight:700;color:#3C454F;')}>12 months</span>
                  <span><span style={sx('color:#54606C;')}>- -</span> delivered</span>
                  <span><span style={sx('color:#2F5C97;font-weight:800;')}>–</span> billed</span>
                  <span style={sx('margin-left:auto;font-weight:700;color:#6B4A12;')}>{cur.delivered && cur.gap != null ? 'gap ' + Math.round((cur.gap / cur.delivered) * 100) + '%' : 'gap –'}</span>
                </span>
                <svg width="100%" height="64" viewBox="0 0 274 64" preserveAspectRatio="none" style={sx('display:block;max-width:100%;')} aria-hidden="true">
                  {ql.spD && <polyline points={ql.spD} fill="none" stroke="#54606C" strokeWidth="1.5" strokeDasharray="4 3" />}
                  <polyline points={ql.spB || '0,62 274,62'} fill="none" stroke="#2F5C97" strokeWidth="2" />
                </svg>
              </div>
              <div style={sx('display:flex;flex-direction:column;gap:0;')}>
                <span style={sx('font-size:14px;font-weight:700;color:#3C454F;padding-bottom:4px;')}>{ql.lst.title}</span>
                {ql.lst.items.map((b, i) => (
                  <div key={i} style={sx('display:grid;grid-template-columns:minmax(0,1fr) auto;gap:8px;align-items:center;padding:6px 0;border-top:1px solid rgba(34,39,46,.08);')}>
                    <span style={sx('min-width:0;display:flex;flex-direction:column;')}>
                      <span style={sx('font-size:16px;font-weight:700;')}>{b.a}</span>
                      <span style={sx('font-size:14px;color:#54606C;')}>{b.b}</span>
                    </span>
                    <span style={sx('height:28px;padding:0 9px;display:inline-flex;align-items:center;border-radius:6px;border:1px solid rgba(34,39,46,.2);font-size:14px;font-weight:700;white-space:nowrap;')}>{b.act}</span>
                  </div>
                ))}
              </div>
              <div style={sx('display:flex;flex-direction:column;gap:2px;padding:8px 10px;border-radius:7px;border:1px solid rgba(34,39,46,.1);')}>
                <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>Best published rate for this code, your region</span>
                <span style={sx('font-size:16px;')}>{p.lens === 'code' ? p.best(cur) : 'Pick a code to compare rates.'}</span>
              </div>
              <div style={sx('display:flex;gap:6px;flex-wrap:wrap;margin-top:auto;')}>
                <a href="#" onClick={(e) => { pre(e); p.primary.go(); }} style={sx(PRI)}>{p.primary.t}</a>
                <a href="#" onClick={(e) => { pre(e); p.askWhy(cur); }} style={sx(SEC)}>Ask Atlas why</a>
                <a href="#" onClick={(e) => { pre(e); exportCsv(); }} style={sx(SEC)}>Export</a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Parts joined by " · ", each in its own span so each translates on its own. */
function parts(ps: string[]) {
  return ps.map((x, i) => (
    <span key={i}>
      {i ? ' · ' : ''}
      <span>{x}</span>
    </span>
  ));
}
